import React, { useEffect, useRef } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link2, Heading2, AlignCenter, AlignLeft, Eraser } from 'lucide-react';

const PLACEHOLDERS = [
  ['{{firstName}}', 'First name'],
  ['{{fullName}}', 'Full name'],
  ['{{className}}', 'Class'],
];

/**
 * Lightweight rich-text editor (contentEditable). The server sanitises the
 * HTML, so only simple formatting survives: bold, italic, lists, links, headings.
 */
export default function RichEditor({ value, onChange, placeholder = 'Write your message…', minHeight = 220 }) {
  const ref = useRef(null);

  // Only push external value changes into the DOM (keeps the caret stable)
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) ref.current.innerHTML = value || '';
  }, [value]);

  const emit = () => onChange(ref.current.innerHTML);

  const cmd = (command, arg) => {
    ref.current.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt('Link address (https://…)');
    if (url) cmd('createLink', /^(https?:|mailto:|tel:)/.test(url) ? url : `https://${url}`);
  };

  const insert = text => cmd('insertText', text);

  const Btn = ({ onClick, icon: Icon, title }) => (
    <button type="button" title={title} onMouseDown={e => e.preventDefault()} onClick={onClick}
      className="rounded p-1.5 text-gray-600 hover:bg-gray-200 hover:text-navy-900"><Icon size={16} /></button>
  );

  return (
    <div className="overflow-hidden rounded-lg border border-gray-300 focus-within:border-navy-500 focus-within:ring-2 focus-within:ring-gold-400/60">
      <div className="flex flex-wrap items-center gap-0.5 border-b bg-gray-50 px-2 py-1">
        <Btn onClick={() => cmd('bold')} icon={Bold} title="Bold" />
        <Btn onClick={() => cmd('italic')} icon={Italic} title="Italic" />
        <Btn onClick={() => cmd('underline')} icon={Underline} title="Underline" />
        <Btn onClick={() => cmd('formatBlock', 'h2')} icon={Heading2} title="Heading" />
        <Btn onClick={() => cmd('insertUnorderedList')} icon={List} title="Bullet list" />
        <Btn onClick={() => cmd('insertOrderedList')} icon={ListOrdered} title="Numbered list" />
        <Btn onClick={addLink} icon={Link2} title="Insert link" />
        <Btn onClick={() => cmd('justifyLeft')} icon={AlignLeft} title="Align left" />
        <Btn onClick={() => cmd('justifyCenter')} icon={AlignCenter} title="Centre" />
        <Btn onClick={() => { cmd('removeFormat'); cmd('formatBlock', 'p'); }} icon={Eraser} title="Clear formatting" />
        <span className="mx-1 h-5 w-px bg-gray-300" />
        <span className="text-xs text-gray-500">Insert:</span>
        {PLACEHOLDERS.map(([tag, label]) => (
          <button key={tag} type="button" onMouseDown={e => e.preventDefault()} onClick={() => insert(tag)}
            className="rounded bg-gold-100 px-2 py-0.5 text-xs font-medium text-gold-800 hover:bg-gold-200">{label}</button>
        ))}
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={emit} onBlur={emit}
        data-placeholder={placeholder}
        className="rich-content bg-white px-4 py-3 text-sm leading-relaxed focus:outline-none"
        style={{ minHeight }} />
    </div>
  );
}
